import { Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AlertTriangleIcon } from '@/components/icons/AlertTriangleIcon';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { useHouseholdDocumentsViewerContext } from '@/modules/documents/components/HouseholdDocumentsViewer/context/HouseholdDocumentsViewer.context';
import { useOptionalAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
import { COLORS } from '@/styles/tokens/colors';

import { AnnotationNavigation } from './components/AnnotationNavigation';
import { EmptyState } from './components/EmptyState';
import { CopyResultButton, CopyResultPopover } from './CopyResultButton';
import { DocumentSearchResult } from './DocumentSearch.types';

interface DocumentSearchResponseProps {
  searchResponse: string;
  paragraphIds: string[];
  showCopyCitationsButton: boolean;
  searchError: boolean;
  annotations: DocumentSearchResult[] | undefined;
  activeAnnotationIndex: number;
  setActiveAnnotationIndex: (index: number) => void;
}

export function DocumentSearchResponse({
  searchResponse,
  paragraphIds,
  showCopyCitationsButton,
  searchError,
  annotations,
  activeAnnotationIndex,
  setActiveAnnotationIndex,
}: DocumentSearchResponseProps) {
  const { updateActiveDocumentId } = useHouseholdDocumentsViewerContext();
  const { setOverrideDocumentId } = useOptionalAnnotatedPDFViewerContext();
  const [displayIndex, setDisplayIndex] = useState(activeAnnotationIndex + 1);

  useEffect(() => {
    setDisplayIndex(activeAnnotationIndex + 1);
  }, [activeAnnotationIndex]);

  const withCitationCopy = useMemo(() => {
    const citation = `[citations=${JSON.stringify(paragraphIds)}]`;
    return `${searchResponse} ${citation}`;
  }, [paragraphIds, searchResponse]);

  const handleSetNewResult = useCallback(
    (newIndex: number) => {
      if (!annotations?.length) return;

      const currResult = annotations?.[activeAnnotationIndex];
      const newResult = annotations?.[newIndex];

      setActiveAnnotationIndex(newIndex);
      if (
        currResult?.documentID !== newResult?.documentID &&
        newResult?.documentID
      ) {
        setOverrideDocumentId(newResult.documentID);
        updateActiveDocumentId({
          documentId: newResult?.documentID,
          shouldClearSearchResults: false,
        });
      }
    },
    [
      activeAnnotationIndex,
      annotations,
      setActiveAnnotationIndex,
      setOverrideDocumentId,
      updateActiveDocumentId,
    ]
  );

  const handlePrevResult = () => {
    if (!annotations?.length) return;
    const newIndex =
      activeAnnotationIndex === 0
        ? annotations.length - 1
        : activeAnnotationIndex - 1;
    handleSetNewResult(newIndex);
  };

  const handleNextResult = () => {
    if (!annotations?.length) return;
    const newIndex = (activeAnnotationIndex + 1) % annotations.length;
    handleSetNewResult(newIndex);
  };

  // Error state
  if (searchError) {
    return (
      <EmptyState
        icon={AlertTriangleIcon}
        text="Error searching document, please try again"
        variant="error"
      />
    );
  }

  // No response and no annotations (undefined or null)
  if (!searchResponse && annotations === undefined) {
    return null;
  }

  // No results found state (empty annotations array but no searchResponse)
  if (!searchResponse && annotations?.length === 0) {
    return (
      <EmptyState
        icon={FileSearch02Icon}
        text="No results found"
        variant="no-results"
      />
    );
  }

  // Annotations only variant - just show navigation header
  if (!searchResponse && annotations?.length) {
    return (
      <AnnotationNavigation
        displayIndex={displayIndex}
        totalCount={annotations.length}
        onPrevious={handlePrevResult}
        onNext={handleNextResult}
        variant="normal"
      />
    );
  }

  const hasAnnotations = Boolean(
    annotations?.length && annotations?.length > 0
  );

  const hasCitations = paragraphIds && paragraphIds.length > 0;
  const shouldShowCitationCopy = showCopyCitationsButton && hasCitations;

  // Full variant with response text
  return (
    <Stack>
      <Stack
        spacing={1}
        sx={{
          p: 2,
          backgroundColor: 'white',
          borderRadius: 1,
          borderBottomLeftRadius: hasAnnotations ? 0 : undefined,
          borderBottomRightRadius: hasAnnotations ? 0 : undefined,
          border: `solid 1px ${COLORS.GRAY[300]}`,
          borderBottom: hasAnnotations ? 'none' : undefined,
        }}
      >
        {/* Main content area */}
        <Stack direction="column" gap={1}>
          {/* Response text */}
          <Typography variant="body-s">Summarized answer</Typography>
          <Typography
            mt={hasAnnotations ? 0 : 1}
            variant="body-s"
            component="div"
            data-testid="DocumentSearchGeneratedResponse"
          >
            {searchResponse}
          </Typography>
        </Stack>

        {/* Footer with disclaimer and copy button */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Typography variant="body-xs">
            Luminary AI can make mistakes. Please double check all outputs for
            accuracy.
          </Typography>
          {shouldShowCitationCopy ? (
            <CopyResultPopover
              searchResponse={searchResponse}
              withCitationCopy={withCitationCopy}
            />
          ) : (
            <CopyResultButton textToCopy={searchResponse} />
          )}
        </Stack>
      </Stack>
      {hasAnnotations && (
        <AnnotationNavigation
          variant="flat-top"
          displayIndex={displayIndex}
          totalCount={annotations?.length ?? 0}
          onPrevious={handlePrevResult}
          onNext={handleNextResult}
        />
      )}
    </Stack>
  );
}
